// Route label for a web-vitals sample.
//
// The beacon can only report the CONCRETE pathname — the app router exposes no
// route-template hook on the client — so the id-stripping happens here, and it
// happens SERVER-side (the client sends the raw pathname). A metric label is a
// time series: `/inbox/txn/9f3c…` as a label mints one series per transaction
// the firm has ever opened, which costs money and makes the p75 unqueryable.
//
// Two bounds, in order. First an ALLOWLIST on the leading segment: only a
// segment that names a real page in `src/app` can start a label, so no amount
// of crafted or stray traffic (a scanner, a stale bookmark, a typo'd link) can
// mint a new series. Then, within a known route, the heuristic: anything a
// human would recognise as a fixed route segment survives, everything else
// collapses to `:id`. Erring toward `:id` only merges series — the failure mode
// is a coarser chart, never an unbounded one.
//
// The allowlist is the load-bearing half. The heuristic alone bounds each
// segment's SHAPE but not the set of first segments, and an attacker-controlled
// pathname is the cheapest cardinality bomb there is.
//
// SECOND CONSUMER: lib/gtag.ts labels GA4 pageviews with this same function.
// The motive there is disclosure rather than cardinality — our authenticated
// URLs carry `?client_id=…` — but the requirement is identical (a bounded set
// of route labels, no entity ids), so it is one allowlist and one test, not
// two that can drift apart.

import { ARTICLE_SLUGS } from './articles/slugs'

/** Bucket for a path that is not a plausible in-app route. */
export const OTHER_ROUTE = '/other'

/** Every /articles/<x> that is not a published article: 404s, scanners, stale
    links. One bucket, so none of them can mint a series. */
export const ARTICLE_OTHER_ROUTE = '/articles/:id'

// NOTE: the Web Vitals beacon this module was written for was removed; the
// normaliser below survives because Google Analytics page-views reuse it, and
// the label bound it enforces (allowlist + id-collapse) is the same obligation.

/**
 * Leading path segments that name a real page.
 *
 * Pinned to `src/app/**\/page.tsx` by web-vitals-route.test.ts — adding a page
 * without adding it here means its vitals silently land in `/other`, and the
 * test is what turns that into a failed build instead of a missing chart.
 */
export const KNOWN_ROUTES: ReadonlySet<string> = new Set([
  'accounts',
  // The public articles surface. Its slugs are longer than
  // MAX_SEGMENT_LENGTH, so a piece labels as `/articles/:id` and the section's
  // traffic aggregates rather than splitting per article. That is deliberate
  // for now, not an oversight: /articles/<anything> renders the 404 under this
  // same layout and fires a page_view, so the path segment IS
  // attacker-controlled and the cap is doing its job. Per-article attribution
  // needs a build-time allowlist of the real slugs, not a longer cap.
  'articles',
  'clients',
  // The Clients CRM demo, behind the clientsDemo flag (ENG-4275).
  'clients-demo',
  // Direct mode's own surface (staging).
  'connections',
  // A client's invite page (ENG-4253); the token is in the fragment, never the path.
  'connect',
  // The firm's connections page, behind the connectionsDemo flag (ENG-4251).
  'connections-demo',
  // Dev-only design-parity harness (404s in production); listed because the
  // pages-on-disk pin requires every page directory to appear here.
  'design',
  'documents',
  'entries',
  'feed',
  'inbox',
  'landing',
  'parties',
  'activity',
  // The two legal documents (ENG-4177). Public, static, and linked from the
  // footer of every marketing page, so they carry real traffic worth naming.
  'privacy',
  'terms',
  // The firm's flag queue (ENG-4059).
  'reviews',
  'sign-in',
  'sign-up',
  'thanks',
  'views',
  'waitlist',
  // The firm's workflows (the procedures runtime): /workflows, a board under its
  // key, a run under that — the key and run id are ids, so the section aggregates.
  'workflows',
])

const MAX_SEGMENTS = 8
const MAX_SEGMENT_LENGTH = 24
const MAX_PATH_LENGTH = 200

/** Fixed segments: lowercase words, digits allowed inside (`v1`, `views2`). */
const FIXED_SEGMENT = /^[a-z][a-z0-9]*(?:[-_.][a-z0-9]+)*$/

export function normalizeVitalsRoute(pathname: string): string {
  if (typeof pathname !== 'string') return OTHER_ROUTE
  // A full URL, a protocol-relative path, or a query/fragment is not something
  // this function should be guessing about.
  const path = pathname.split('?')[0]!.split('#')[0]!
  if (!path.startsWith('/') || path.startsWith('//') || path.length > MAX_PATH_LENGTH) {
    return OTHER_ROUTE
  }
  // A trailing slash is the same page, and Next serves it as such. Splitting it
  // in yields an empty final segment, which is not a fixed segment — so `/inbox/`
  // would otherwise label as `/inbox/:id` and split the route's p75 in two.
  const trimmed = path.replace(/\/+$/, '')
  if (trimmed === '') return '/'

  const segments = trimmed.slice(1).split('/')
  if (segments.length > MAX_SEGMENTS) return OTHER_ROUTE
  if (!KNOWN_ROUTES.has(segments[0]!)) return OTHER_ROUTE

  // Articles are decided by MEMBERSHIP, before the generic segment heuristic
  // gets a say. A published slug reports as itself — GA4 builds Views, Landing
  // page and the whole acquisition model from this string, so collapsing it is
  // what made "which article brought this session in" unanswerable. Anything
  // else under /articles/ lands in one bucket regardless of how respectable its
  // shape is, which is the opposite of what the length cap did here: a 23-char
  // invented slug passed through while the real 27-char article collapsed.
  if (segments[0] === 'articles' && segments.length > 1) {
    return segments.length === 2 && ARTICLE_SLUGS.has(segments[1]!)
      ? `/articles/${segments[1]}`
      : ARTICLE_OTHER_ROUTE
  }

  const normalized = segments.map((segment) =>
    segment.length > 0 && segment.length <= MAX_SEGMENT_LENGTH && FIXED_SEGMENT.test(segment)
      ? segment
      : ':id',
  )
  return `/${normalized.join('/')}`
}
